<template>
    <div class="card col-md-12 mx-auto">
        <div class="btn-group" role="group" aria-label="Basic example">
            <button type="button"
                    class="btn btn-primary"
                    @click="redirectToList">
                <i class="fas fa-th-list"></i> {{ $t('backToList') }}
            </button>
        </div>
        <div class="card-header card-header bg-primary text-white h4 m-0">
            <page-title :title="$t('finance.tariffEntity.title') + ' / ' + (id ? $t('buttons.edit.btnText') : $t('buttons.create.btnText_a'))"></page-title>
        </div>

        <div class="card-body">
            <div class="col-12" v-if="hasSelectedItems" v-cloak>
                <div v-for="(value, key) in model.selectedItems" :key='key'>
                    <div class="card bg-light mb-4" v-if="key !== '' && Array.isArray(value) && value.length > 0">
                        <div class="card-header">
                            <div class="btn-toolbar justify-content-between" role="toolbar">
                                <div class="row">
                                    <button @click.stop="removeGroup(key)" type="button"
                                            class="btn btn-danger fas fa-trash-alt">
                                        {{ $t('buttons.delete.btnText') }}
                                    </button>
                                    <h5 class="card-title ml-2 pt-2"> {{ key }}</h5>
                                </div>
                                <div class="input-group">
                                    <div class="input-group mb-2 mr-sm-2">
                                        <div class="input-group-prepend">
                                            <div class="input-group-text"><i class="fa fa-search"></i></div>
                                        </div>
                                        <input type="text" class="form-control ml-2" name="query" v-model="searchQuerySelectedItems">
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="card-body" v-cloak>
                            <custom-grid :data="value"
                                         :columns="selectedItemsGridColumns"
                                         :filter-key="searchQuerySelectedItems"
                                         :captions="gridCaptions"
                                         :gridName="'SelectedTariffItems'"
                                         :editableColumns="editableColumns"
                                         :btns="selectedItemsBtns"
                                         :usedForTariffItem="true"
                                         :options="tariffItemOptions">
                            </custom-grid>
                        </div>
                    </div>

                </div>
            </div>

            <div class="row">
                <div class="form-group col-lg-4 col-sm-12">
                    <label class="font-weight-bold required" :for="'name' + _uid">{{ $t('registers.nomenclatures.gridHeaders.name') }}</label>
                    <input type="text" class="form-control"
                           v-model="model.name"
                           v-validate="'required'"
                           :data-vv-as="$t('registers.nomenclatures.gridHeaders.name')"
                           :name="'name' + _uid"
                           :id="'name' + _uid">
                    <span class="text-danger" v-if="errors.has('name' + _uid)">{{ errors.first('name' + _uid) }}</span>
                </div>

                <div class="form-group col-lg-4 col-sm-12">
                    <label class="font-weight-bold required" :for="'nameEn' + _uid">{{ $t('registers.nomenclatures.gridHeaders.nameEn') }}</label>
                    <input type="text" class="form-control"
                           v-model="model.nameEn"
                           :name="'nameEn' + _uid"
                           :id="'nameEn' + _uid">
                </div>

                <div class="form-group col-lg-4 col-sm-12">
                    <label class="font-weight-bold" :for="'description' + _uid">{{ $t('registers.nomenclatures.gridHeaders.description') }}</label>
                    <input type="text" class="form-control"
                           v-model="model.description"
                           :name="'description' + _uid"
                           :id="'description' + _uid">
                </div>
            </div>

            <div class="row">
                <div class="form-group col-12">
                    <label class="font-weight-bold" :for="'tariff-contract' + _uid">{{ $t('contract.cardTitle') }}</label>
                    <div>
                        <kendo-datasource ref="contractsDatasource"
                                          :transport="contractsTransportSettings"
                                          :schema-model-id="'id'"
                                          :schema-model-fields="contractsFields"
                                          :schema-data="'items'"
                                          :error="dataSourceError">
                        </kendo-datasource>

                        <kendo-multicolumncombobox :data-source-ref="'contractsDatasource'"
                                                   :data-text-field="'displayName'"
                                                   :data-value-field="'id'"
                                                   :name="'tariff-contract' + _uid"
                                                   :id="'tariff-contract' + _uid"
                                                   v-model="model.contractId"
                                                   :placeholder="$t('selectContract')"
                                                   :no-data-template="$t('missingData')"
                                                   class="form-control"
                                                   :height="600"
                                                   :filter="'contains'">
                            <kendo-multicolumncombobox-column :field="'number'"
                                                              :title="$t('contract.number')">
                            </kendo-multicolumncombobox-column>
                            <kendo-multicolumncombobox-column :field="'name'"
                                                              :title="$t('contract.name')">
                            </kendo-multicolumncombobox-column>

                            <kendo-multicolumncombobox-column :field="'externalSupplier'"
                                                              :title="$t('contract.externalSupplier')">
                            </kendo-multicolumncombobox-column>
                            <kendo-multicolumncombobox-column :field="'externalSupplierActive'"
                                                              :template="activeCellTemplate"
                                                              :title="$t('finance.tariffEntity.gridHeaders.active')">
                            </kendo-multicolumncombobox-column>
                            <kendo-multicolumncombobox-column :field="'active'"
                                                              :template="validCellTemplate"
                                                              :title="$t('finance.tariffEntity.gridHeaders.valid')">
                            </kendo-multicolumncombobox-column>
                            <kendo-multicolumncombobox-column :field="'validFromUtc'"
                                                              :title="$t('contract.validFrom')"
                                                              :template="validFromTemplate">
                            </kendo-multicolumncombobox-column>
                            <kendo-multicolumncombobox-column :field="'validToUtc'"
                                                              :title="$t('contract.validTo')"
                                                              :template="validToTemplate">
                            </kendo-multicolumncombobox-column>
                        </kendo-multicolumncombobox>
                    </div>
                </div>

                <div class="form-group col-lg-6 col-md-12">
                    <label class="font-weight-bold" :for="'allowedEventForms' + _uid">{{ $t('registers.events.eventForms') }}</label>
                    <multi-select v-model="model.allowedEventForms"
                                  :name="'allowedEventForms' + _uid"
                                  :id="'allowedEventForms' + _uid"
                                  :load-by-parent-sort="true"
                                  :parent-sort="nomenclature.EventForm"
                                  :fieldName="'allowedEventForms'"
                                  :required="false">
                    </multi-select>
                </div>

                <div class="form-group col-lg-6 col-md-12">
                    <label class="font-weight-bold" :for="'defaultForEventForms' + _uid">Да се използва по подразбиране при</label>
                    <multi-select v-model="model.defaultForEventForms"
                                  :name="'defaultForEventForms' + _uid"
                                  :id="'defaultForEventForms' + _uid"
                                  :load-by-parent-sort="true"
                                  :parent-sort="nomenclature.EventForm"
                                  :fieldName="'defaultForEventForms'"
                                  :required="false">
                    </multi-select>
                </div>
            </div>

            <div class="col-12 form-group" v-if="hasSelectedItems && !saving" v-cloak>
                <button class="btn btn-primary fas fa-save" @click.stop="save">
                    {{ $t('buttons.save.btnText')}}
                </button>
            </div>
        </div>

        <div class="clearfix"></div>

        <div class="card-body" v-if="hasItems" v-cloak>
            <div v-cloak>
                <div class="input-group col-sm-3 float-right">
                    <div class="input-group-addon fa fa-search" style="display:table-cell"></div>
                    <input class="form-control fa fa-search" name="query" v-model="searchQuery">
                </div>
            </div>
            <custom-grid :data="itemsNomenclature"
                         :columns="gridColumns"
                         :filter-key="searchQuery"
                         :captions="gridCaptions"
                         :gridName="'TariffItemsNomenclature'"
                         :btns="tariffItemRelationsBtns">
            </custom-grid>
        </div>
        <div class="card-body" v-else v-cloak>
            <div class="alert alert-warning" role="alert">
                <h4>
                    Липсват данни.
                </h4>
            </div>
        </div>

        <JsonFormatter v-model="tariffItemsLinks" :show.sync="showTariffItemsLinks"
                       :titleKey="'buttons.links.tooltipText'"
                       @onClose="onTariffItemsLinksClose">
        </JsonFormatter>
    </div>
</template>

<script>
    import Vue from 'vue'
    import PageTitle from '@/components/common/PageTitle'
    import CustomGrid from "@/components/grid/CustomGrid"
    import NomenclatureMultiSelect from '@/components/dropdown/MultiSelect'
    import bus from '@/components/sidemenu/event-bus'
    import { SelectedTariffItem, TariffEntity, InputEvent } from '@/models/models'
    import { NomenclatureParentBySort } from '@/models/enums'
    import tariffService from '@/services/tariff.service'
    import JsonFormatter from "@/components/common/json.formatter.modal.vue";
    import { mapGetters } from 'vuex';

    import {
        DropdownsInstaller
    } from '@progress/kendo-dropdowns-vue-wrapper'

    Vue.use(DropdownsInstaller)

    export default {
        inject: ['$validator'],
        props: {
            id: Number,
            isCopy: {
                type: Boolean,
                default: false
            },
        },
        components: {
            PageTitle,
            CustomGrid,
            'multi-select': NomenclatureMultiSelect,
            JsonFormatter
        },
        data() {
            return {
                nomenclature: NomenclatureParentBySort,
                model: {},
                saving: false,
                loaded: false,
                itemsNomenclature: [],
                tariffItemsLinks: '',
                showTariffItemsLinks: false,
                gridCaptions: {
                    id: 'finance.tariffItem.gridHeaders.id',
                    itemNumber: 'finance.tariffItem.gridHeaders.itemNumber',
                    groupName: 'finance.tariffItem.gridHeaders.groupName',
                    groupNameEn: 'finance.tariffItem.gridHeaders.groupName',
                    name: 'finance.tariffItem.gridHeaders.name',
                    nameEn: 'finance.tariffItem.gridHeaders.name',
                    description: 'finance.tariffItem.gridHeaders.description',
                    specification: 'finance.tariffItem.gridHeaders.specification',
                    package: 'finance.tariffItem.gridHeaders.package',
                    code: 'finance.tariffItem.gridHeaders.code',
                    deactivated: 'finance.tariffItem.gridHeaders.deactivated',
                    deleted: 'finance.tariffItem.gridHeaders.deleted',
                    active: 'finance.tariffItem.gridHeaders.active',
                    checked: '',
                    qty: 'finance.tariffItem.gridHeaders.qty',
                    measure: 'finance.tariffItem.gridHeaders.measure',
                    singlePrice: 'finance.tariffItem.gridHeaders.singlePrice',
                    singlePriceWithTax: 'finance.tariffItem.gridHeaders.singlePriceWithTax',
                    maxPrice: 'finance.tariffItem.gridHeaders.maxPrice',
                    total: 'finance.tariffItem.gridHeaders.total',
                    totalWithTax: 'finance.tariffItem.gridHeaders.totalWithTax'
                },
                editableColumns: ['qty', 'singlePrice', 'singlePriceWithTax', 'maxPrice', 'measure'],
                searchQuery: '',
                searchQuerySelectedItems: '',
                selectedItemsBtns: [{
                    name: 'remove',
                    text: '',
                    tooltip: this.$t('buttons.delete.btnText'),
                    class: 'btn btn-danger',
                    icon: 'fas fa-trash-alt',
                    show: true,
                    clickHandler: this.removeSelectedItemClick
                }],
                tariffItemRelationsBtns: [{
                    name: 'tariffItemRelations',
                    text: '',
                    tooltip: this.$t('buttons.links.tooltipText'),
                    class: 'btn btn-info',
                    icon: 'fas fa-link',
                    show: this.$store.getters.claims.dataReferences.read,
                    clickHandler: this.tariffItemRelationsClick
                }],
                contractsTransportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + `/api/contract/forExternalSuppliers?noTariff=${this.isNew}&tariffId=${this.id}`,
                        dataType: 'json',
                        type: 'GET',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    }
                },
                contractsFields: {
                    id: { name: 'id', from: 'id', type: 'number', nullable: false },
                    number: { name: 'number', from: 'number', type: 'string', nullable: true },
                    name: { name: 'name', from: 'name', type: 'string', nullable: true },
                    validFromUtc: { name: 'validFromUtc', from: 'validFromUtc', type: 'date', nullable: true },
                    validToUtc: { name: 'validToUtc', from: 'validToUtc', type: 'date', nullable: true },
                    externalSupplier: { name: 'externalSupplier', from: 'externalSupplier', type: 'string', nullable: true },
                    displayName: { name: 'displayName', from: 'displayName', type: 'string', nullable: true }
                },
                tariffItemOptions: {
                    qty: { inputType: 'number', min: 0, max: 999999999, step: 1, required: true, size: 100 },
                    singlePrice: { inputType: 'number', min: 0, max: 999999999, step: 0.01, size: 100 },
                    singlePriceWithTax: { inputType: 'number', min: 0, max: 999999999, step: 0.01, size: 100 },
                    maxPrice: { inputType: 'number', min: 0, max: 999999999, step: 0.01, size: 100 }
                }
            }
        },
        created() {
            this.bus = bus
            this.bus.$on('checkbox-changed', (item, gridName) => {
                if (gridName === 'TariffItemsNomenclature') {
                    this.processTariffItem(item)
                }

                if (gridName === 'SelectedTariffItems') {
                    this.processSelectedItem(item)
                }
            })

            this.bus.$on('custom-grid-item-input-event', (event, item, gridName) => {
                if (gridName !== 'SelectedTariffItems') {
                    return
                }

                // Handle only input event
                if (event.type === 'change') {
                    return
                }

                const inputEvent = new InputEvent(event)
                if (inputEvent.name === 'qty') {
                    this.calcTotal(item, Number(item.qty), Number(item.singlePrice))
                    this.calcTotalWithTax(item, Number(item.qty), Number(item.singlePriceWithTax))
                } else if (inputEvent.name === 'singlePrice') {
                    this.calcTotal(item, Number(item.qty), Number(item.singlePrice))

                    item.singlePriceWithTax = (item.singlePrice * 1.2).toFixed(2)
                    this.calcTotalWithTax(item, Number(item.qty), Number(item.singlePriceWithTax))
                } else if (inputEvent.name === 'singlePriceWithTax') {
                    this.calcTotalWithTax(item, Number(item.qty), Number(item.singlePriceWithTax))
                }
            })
        },
        beforeDestroy() {
            this.bus.$off('checkbox-changed')
            this.bus.$off('custom-grid-item-input-event')
        },
        mounted: function () {
            if (!this.isNew && !this.isCopy) {
                this.load()
            } else if (this.isCopy) {
                this.loadForCopy()
            } else {
                this.model = new TariffEntity()
            }

            this.loadItemsNomenclature()
        },
        computed: {
            ...mapGetters(['isEnVersion', 'dateTimeFormat']),
            customDateTimeFormat() {
                return `{0:${this.dateTimeFormat}}`
            },
            isNew: function () {
                return this.isCopy || this.id === undefined
            },
            token: function () {
                return this.$store.getters.authToken
            },
            hasItems() {
                return Array.isArray(this.itemsNomenclature) && this.itemsNomenclature.length;
            },
            hasSelectedItems() {
                for (var key in this.model.selectedItems) {
                    if (!this.model.selectedItems.hasOwnProperty(key) || key === '') {
                        continue;
                    }

                    var groupObject = this.model.selectedItems[key];
                    for (var key1 in groupObject) {
                        if (!groupObject.hasOwnProperty(key1) || key1 === '') {
                            continue;
                        }

                        if (groupObject.length > 0) {
                            return true;
                        }
                    }
                }

                return false;
            },
            selectedItemsGridColumns() {
                return this.isEnVersion
                    ? ['qty', 'measure', 'singlePrice', 'singlePriceWithTax', 'maxPrice', 'total', 'totalWithTax', 'itemNumber', 'nameEn', 'description', 'specification', 'package', 'code']
                    : ['qty', 'measure', 'singlePrice', 'singlePriceWithTax', 'maxPrice', 'total', 'totalWithTax', 'itemNumber', 'name', 'description', 'specification', 'package', 'code'];
            },
            gridColumns() {
                return this.isEnVersion
                    ? ['checked', 'itemNumber', 'groupNameEn', 'nameEn', 'description', 'specification', 'package', 'code']
                    : ['checked', 'itemNumber', 'groupName', 'name', 'description', 'specification', 'package', 'code'];
            }
        },
        methods: {
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            load() {
                tariffService.load(this.id)
                    .then((response) => {
                        this.model = response.data.model;
                        this.loaded = true
                    }).catch(error => {
                        this.loaded = true

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    })
            },
            loadForCopy() {
                tariffService.loadForCopy(this.id)
                    .then((response) => {
                        this.model = response.data.model;
                        this.loaded = true
                    }).catch(error => {
                        this.loaded = true

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    })
            },
            loadItemsNomenclature() {
                tariffService.loadItemsNomenclature(false, this.id)
                    .then((response) => {
                        this.itemsNomenclature = response.data.items;
                    }).catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    })
            },
            redirectToList() {
                this.$router.push({ path: '/tariffGroups' });
            },
            processTariffItem(item) {
                if (item.checked) {
                    this.addToSelectedItems(item);
                } else {
                    this.removeFromSelectedItems(item);
                }
            },
            processSelectedItem(item) {

            },
            addToSelectedItems(item) {
                if (!item.checked) {
                    // The item is not selected. Show warning and return

                    this.$vs.notify({
                        title: 'Добавяне на артикул',
                        text: 'Артикулът не е избран!',
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                if (!Array.isArray(this.model.selectedItems[item.groupName])) {
                    this.$set(this.model.selectedItems, item.groupName, []);
                    this.model.selectedItems[item.groupName].push(new SelectedTariffItem(item));

                    return;
                }

                if (!this.model.selectedItems[item.groupName].length) {
                    this.model.selectedItems[item.groupName].push(new SelectedTariffItem(item));

                    return;
                }


                for (var i = 0; i < this.model.selectedItems[item.groupName].length; i++) {
                    if (this.model.selectedItems[item.groupName][i].id === item.id) {
                        this.$vs.notify({
                            title: 'Добавяне на артикул',
                            text: 'Артикулът вече е добавен',
                            color: 'warning',
                            time: this.$store.getters.notificationDuration
                        })

                        return;
                    }
                }

                this.model.selectedItems[item.groupName].push(new SelectedTariffItem(item));
            },
            removeFromSelectedItems(item) {
                if (item.checked) {
                    // The item is selected. Show warning and return
                    this.$vs.notify({
                        title: 'Премахване на артикул',
                        text: 'Артикулът е избран!',
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                kendo.confirm(`Сигурни ли сте, че искате да премахнете артикул "${item.itemNumber}/${item.name}"?`).then(function () {
                    if (item.groupName in this.model.selectedItems && Array.isArray(this.model.selectedItems[item.groupName])
                        && this.model.selectedItems[item.groupName].length > 0) {
                        this.model.selectedItems[item.groupName] = this.model.selectedItems[item.groupName].filter(el => el.id !== item.id);
                    }
                }.bind(this));
            },
            removeSelectedItemClick: function (args) {
                if (args.groupName in this.model.selectedItems && Array.isArray(this.model.selectedItems[args.groupName])
                    && this.model.selectedItems[args.groupName].length > 0) {
                    kendo.confirm(`Сигурни ли сте, че искате да премахнете артикул "${args.itemNumber}/${args.name}"?`).then(function () {
                        this.model.selectedItems[args.groupName] = this.model.selectedItems[args.groupName].filter(el => el.id !== args.id);
                        this.uncheckNomenclatureItem(args.id);
                    }.bind(this));
                }
            },
            uncheckNomenclatureItem(id) {
                if (!id) {
                    return;
                }
                if (Array.isArray(this.itemsNomenclature) && this.itemsNomenclature.length > 0) {
                    for (var i = 0; i < this.itemsNomenclature.length; i++) {
                        if (this.itemsNomenclature[i].id === id) {
                            if (this.itemsNomenclature[i].checked) {
                                this.itemsNomenclature[i].checked = false
                            }
                            return;
                        }
                    }
                }
            },
            removeGroup(key) {
                kendo.confirm('Сигурни ли сте, че искате да изтриете цялата група "' + key
                    + '"?').then(function () {
                        if (key in this.model.selectedItems && Array.isArray(this.model.selectedItems[key])
                            && this.model.selectedItems[key].length > 0
                            && Array.isArray(this.itemsNomenclature)
                            && this.itemsNomenclature.length > 0) {
                            var selectedItemsToRemove = {};
                            for (let i = 0; i < this.model.selectedItems[key].length; i++) {
                                let item = this.model.selectedItems[key][i];
                                selectedItemsToRemove[item.id] = item.id;
                            }

                            for (let i = 0; i < this.itemsNomenclature.length; i++) {
                                let nomItem = this.itemsNomenclature[i];
                                if (!nomItem.checked) {
                                    continue;
                                }

                                if (nomItem.id in selectedItemsToRemove) {
                                    nomItem.checked = false;

                                    delete selectedItemsToRemove[nomItem.id];
                                }

                                if (!Object.keys(selectedItemsToRemove).length) {
                                    this.$delete(this.model.selectedItems, key);
                                    return;
                                }
                            }
                        }
                    }.bind(this));
            },
            validCellTemplate: function (dataItem) {
                let templateHtml =
                    '<span class="badge ' + (dataItem.active ? 'badge-success' : 'badge-danger') + '">' + kendo.htmlEncode(dataItem.active ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            activeCellTemplate: function (dataItem) {
                let templateHtml =
                    '<span class="badge ' + (dataItem.externalSupplierActive ? 'badge-success' : 'badge-danger') + '">' + kendo.htmlEncode(dataItem.externalSupplierActive ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            validFromTemplate: function (dataItem) {
                if (!dataItem.validFromUtc) return '';

                return this.$moment(dataItem.validFromUtc).format('DD.MM.YYYY HH:mm');
            },
            validToTemplate: function (dataItem) {
                if (!dataItem.validToUtc) return '';

                return this.$moment(dataItem.validToUtc).format('DD.MM.YYYY HH:mm');
            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            save() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        this.saving = true;

                        if (this.isNew) {
                            tariffService.create(this.model)
                                .then((response) => {
                                    this.saving = false;

                                    this.$vs.notify({
                                        title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                                        text: this.$helpers.responseMessage(response),
                                        color: 'success',
                                        time: this.$store.getters.notificationDuration,
                                    })

                                    this.redirectToList();
                                }).catch(error => {
                                    this.saving = false;

                                    this.$vs.notify({
                                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                        text: this.$helpers.errorText(error),
                                        color: 'danger',
                                        time: this.$store.getters.notificationDuration
                                    })
                                })
                        } else {
                            tariffService.update(this.model)
                                .then((response) => {
                                    this.saving = false;

                                    this.$vs.notify({
                                        title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                                        text: this.$helpers.responseMessage(response),
                                        color: 'success',
                                        time: this.$store.getters.notificationDuration
                                    })

                                    this.redirectToList();
                                }).catch(error => {
                                    this.saving = false;

                                    this.$vs.notify({
                                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                        text: this.$helpers.errorText(error),
                                        color: 'danger',
                                        time: this.$store.getters.notificationDuration
                                    })
                                })
                        }
                    }
                });
            },
            calcTotal(item, qty, price) {
                item.total = ((isNaN(qty) ? 0 : qty) * (isNaN(price) ? 0 : price)).toFixed(2);
            },
            calcTotalWithTax(item, qty, priceWithTax) {
                item.totalWithTax = ((isNaN(qty) ? 0 : qty) * (isNaN(priceWithTax) ? 0 : priceWithTax)).toFixed(2);
            },
            tariffItemRelationsClick(args) {
                this.tariffItemsLinks = '';
                tariffService.checkLinks(this.id, args.id)
                    .then(response => {
                        if (response.data) {
                            this.tariffItemsLinks = response.data;
                        }

                        this.showTariffItemsLinks = true;
                    }).catch(e => {
                        console.log(e);
                    })
            },
            onTariffItemsLinksClose() {
                this.tariffItemsLinks = '';
                this.showTariffItemsLinks = false
            },
        }
    }

</script>

<style scoped>
</style>
